import React from "react";
import { Link } from "react-router-dom";
import { Lock, ShieldCheck, MapPin, TrendingUp, Building2, Eye, Sparkles } from "lucide-react";
import { Pill } from "../common/Primitives";
import VerifiedBadge from "../common/VerifiedBadge";
import { API_BASE } from "../../lib/api";
import { Flame } from "lucide-react";
import TrustBadge from "./TrustBadge";

function formatINR(v) {
  if (!v && v !== 0) return "—";
  const n = Number(v);
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(1)} L`;
  return `₹${n.toLocaleString("en-IN")}`;
}

function LogoThumb({ startupId, hasLogo, size = 44 }) {
  return (
    <div
      className="rounded-md border border-white/10 bg-ink-surface/60 flex items-center justify-center overflow-hidden shrink-0"
      style={{ width: size, height: size }}
      data-testid={`startup-card-logo-${startupId}`}
    >
      {hasLogo ? (
        <img
          src={`${API_BASE}/marketplace/startup/${startupId}/logo`}
          alt=""
          className="max-w-full max-h-full object-contain"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      ) : (
        <Building2 size={18} className="text-ink-muted/60" />
      )}
    </div>
  );
}

export default function StartupCard({ startup, onExpressInterest, actionLabel = "View Details" }) {
  const locked = startup.locked;
  const score = startup.match_score;
  return (
    <div className="glass rounded-lg card-hover p-5 flex flex-col" data-testid={`startup-card-${startup.startup_id}`}>
      <div className="flex items-start justify-between mb-3 gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <LogoThumb startupId={startup.startup_id} hasLogo={startup.has_logo} />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className={`font-heading text-ink text-lg truncate ${locked ? "italic" : ""}`} data-testid="startup-card-name">
                {startup.startup_name}
              </div>
              {startup.verified ? (
                <Pill tone="gold"><ShieldCheck size={11} className="mr-1" /> Verified</Pill>
              ) : startup.is_listed === false ? (
                <Pill tone="muted" testId={`startup-card-not-listed-${startup.startup_id}`}>Not Listed</Pill>
              ) : null}
              {startup.admin_force_visible ? (
                <Pill tone={startup.kyc_status === "Approved" ? "success" : "warn"} testId={`startup-card-kyc-${startup.startup_id}`}>
                  {startup.kyc_status === "Approved" ? "KYC Approved" : "KYC Pending"}
                </Pill>
              ) : null}
              {startup.handpicked_by_venturaz ? (
                <Pill tone="gold" testId={`startup-card-handpicked-${startup.startup_id}`}>
                  <Sparkles size={10} className="mr-1" /> VenturaZ Handpicked
                </Pill>
              ) : null}
              {startup.trust_badge?.tier && (
                <VerifiedBadge badge={startup.trust_badge} size="sm" inline />
              )}
            </div>
            <div className="text-xs text-ink-muted mt-1 font-mono uppercase tracking-wider">{startup.sector} · {startup.business_stage}</div>
            {!locked && startup.trust_score !== null && startup.trust_score !== undefined ? (
              <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid={`startup-card-trust-${startup.startup_id}`}>
                <TrustBadge total={startup.trust_score} label={startup.trust_label} />
                {typeof startup.streak_weeks === "number" && startup.streak_weeks >= 4 ? (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 border border-orange-300 text-orange-700 text-[10px] font-mono"
                    title={`Trust score has been non-decreasing for ${startup.streak_weeks} weeks`}
                    data-testid={`startup-card-streak-${startup.startup_id}`}
                  >
                    <Flame size={9} />
                    {startup.streak_weeks}w streak
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        {typeof score === "number" ? (
          <div className="match-badge px-2.5 py-1 text-[11px] font-mono flex items-center gap-1 shrink-0" data-testid="match-score-badge">
            <TrendingUp size={11} /> {score}
          </div>
        ) : null}
      </div>

      <div className="text-sm text-ink-soft leading-relaxed line-clamp-2 min-h-[40px]">
        {startup.one_line_pitch || "—"}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div>
          <div className="overline">Funding Ask</div>
          <div className="font-mono text-ink text-sm mt-0.5">
            {locked ? <span className="locked-blur">₹XX Cr</span> : formatINR(startup.funding_ask)}
          </div>
        </div>
        <div>
          <div className="overline">Type</div>
          <div className="font-mono text-ink text-sm mt-0.5">{startup.funding_type || "—"}</div>
        </div>
        <div>
          <div className="overline">Revenue</div>
          <div className="font-mono text-ink text-sm mt-0.5">
            {locked ? <span className="locked-blur">₹X Cr</span> : formatINR(startup.annual_revenue)}
          </div>
        </div>
        <div>
          <div className="overline">City</div>
          <div className="font-mono text-ink text-sm mt-0.5 flex items-center gap-1"><MapPin size={11} className="text-gold/60" /> {startup.city || "—"}</div>
        </div>
      </div>

      {locked && (
        <div className="mt-4 glass-dark px-3 py-2 text-xs text-ink-soft flex items-center gap-2 rounded-md" data-testid="locked-indicator">
          <Lock size={12} className="text-gold" />
          Restricted data. Complete investor approval to unlock.
        </div>
      )}

      <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between">
        <div className="text-[11px] font-mono text-ink-faint flex items-center gap-2">
          <span>Profile · {startup.profile_completeness ?? 0}%</span>
          {!locked && startup.peer_views > 0 && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px]"
              title={`${startup.peer_views} of your peers viewed this`}
              data-testid={`peer-views-${startup.startup_id}`}
            >
              <Eye size={9} /> {startup.peer_views} peer{startup.peer_views === 1 ? "" : "s"} viewed
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/startup/${startup.startup_id}`}
            className="text-xs font-heading text-ink-soft hover:text-gold transition-colors"
            data-testid={`view-startup-${startup.startup_id}`}
          >
            {actionLabel} →
          </Link>
          {onExpressInterest ? (
            <button
              onClick={() => onExpressInterest(startup)}
              className="px-3 py-1.5 bg-gold/10 border border-gold/40 text-gold text-xs font-heading hover:bg-gold hover:text-ink-bg transition-colors"
              data-testid={`express-interest-${startup.startup_id}`}
            >
              Express Interest
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
